import { getTranslations } from 'next-intl/server';

import {
  apiMindbox,
  MINDBOX_OPERATIONS,
  MindboxResponseBody,
  ResponseError
} from '@/fo/api/mindbox';

export type PointOfContact = 'Sms' | 'Email' | 'Whatsapp';

const getCustomerSubscriptions = async ({
  email,
  ids
}: {
  email: string | null;
  ids: { bitrixClientId: string };
}): Promise<
  | MindboxResponseBody[typeof MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS]
  | ResponseError
  | null
> => {
  return await apiMindbox(MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS, {
    customer: {
      ...(email && { email }),
      ids: {
        websiteID: ids.bitrixClientId
      }
    }
  });
};

const getCustomerSubscriptionsByEmail = async ({
  email
}: {
  email: string;
}): Promise<
  | MindboxResponseBody[typeof MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL]
  | ResponseError
  | null
> => {
  return await apiMindbox(
    MINDBOX_OPERATIONS.GET_CUSTOMER_SUBSCRIPTIONS_BY_EMAIL,
    {
      customer: {
        email
      }
    }
  );
};

const editCustomerSubscriptions = async ({
  email,
  ids,
  subscriptions
}: {
  email: string | null;
  ids: { bitrixClientId: string };
  subscriptions: Array<{
    pointOfContact: PointOfContact;
    isSubscribed: boolean;
    topic?: string;
  }>;
}): Promise<
  | MindboxResponseBody[typeof MINDBOX_OPERATIONS.SUBSCRIPTION]
  | ResponseError
  | null
> => {
  return await apiMindbox(MINDBOX_OPERATIONS.SUBSCRIPTION, {
    customer: {
      ...(email && { email }),
      subscriptions,
      ids
    }
  });
};

const subscribeCustomer = async ({ email }: { email: string }) => {
  return await apiMindbox(MINDBOX_OPERATIONS.SUBSCRIBE_CUSTOMER_IN_FOOTER, {
    customer: {
      email,
      subscriptions: [
        {
          pointOfContact: 'Email',
          isSubscribed: true
        }
      ]
    }
  });
};

const unsubscribeAll = async ({
  email,
  ids,
  channels
}: {
  email: string | null;
  ids: { bitrixClientId: string };
  channels: PointOfContact[];
}): Promise<
  | MindboxResponseBody[typeof MINDBOX_OPERATIONS.SUBSCRIPTION]
  | ResponseError
  | null
> => {
  const subscriptions = channels.map((channel) => ({
    pointOfContact: channel,
    isSubscribed: false
  }));

  return await editCustomerSubscriptions({
    email,
    ids,
    subscriptions
  });
};

const getProductSubscriptions = async ({
  email,
  id,
  hasPromoSubscription
}: {
  email: string;
  id: string;
  hasPromoSubscription: boolean;
}): Promise<
  | MindboxResponseBody[typeof MINDBOX_OPERATIONS.GET_PRODUCT_SUBSCRIPTIONS]
  | ResponseError
  | null
> => {
  const t = await getTranslations();

  return await apiMindbox(MINDBOX_OPERATIONS.GET_PRODUCT_SUBSCRIPTIONS, {
    customer: {
      email,
      ...(hasPromoSubscription && {
        subscriptions: [
          {
            pointOfContact: 'Email',
            isSubscribed: true
          },
          {
            topic: t('multisubscription.topics.specialOffers'),
            pointOfContact: 'Email',
            isSubscribed: true
          }
        ]
      })
    },
    addProductToList: {
      product: {
        ids: {
          bitrixProducts: id
        }
      }
    }
  });
};

export const subscriptionsMindboxService = {
  getCustomerSubscriptions,
  getCustomerSubscriptionsByEmail,
  getProductSubscriptions,
  editCustomerSubscriptions,
  subscribeCustomer,
  unsubscribeAll
};
